/**
 * useDemoMode — drives the global "demo mode" banner in the Shell.
 *
 * Answers the question: "has this merchant connected all three required
 * integrations?" If not, every data surface that falls back to fixtures
 * will be showing demo data, and the Shell renders a banner saying so.
 *
 * Required set:
 *   shopify    — storefront ingest (no Shopify = no orders to triage)
 *   shiprocket — fulfilment + tracking
 *   delhivery  — secondary courier + tracking
 *
 * Reactivity:
 *   - Initial fetch on mount via a TanStack Query so the rest of the app
 *     can read it through the cache.
 *   - Supabase realtime subscription on `user_integrations` filtered by
 *     user_id, so connecting/disconnecting an integration flips the demo
 *     flag immediately without a reload.
 *   - Window-focus refetch as a fallback for browsers/networks that drop
 *     the realtime channel.
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const REQUIRED = ["shopify", "shiprocket", "delhivery"] as const;

interface DemoModeResult {
  isDemo:         boolean;
  connectedCount: number;
  total:          number;
  loading:        boolean;
}

async function fetchConnectedCount(userId: string): Promise<number> {
  const { data, error } = await supabase
    .from("user_integrations")
    .select("provider,status")
    .eq("user_id", userId);
  if (error) {
    console.warn("useDemoMode: integrations fetch failed:", error.message);
    return 0;
  }
  const connected = new Set(
    (data ?? [])
      .filter((r) => r.status === "connected")
      .map((r) => r.provider),
  );
  return REQUIRED.filter((p) => connected.has(p)).length;
}

export function useDemoMode(): DemoModeResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const q = useQuery({
    queryKey:  ["demo-mode", userId],
    queryFn:   () => fetchConnectedCount(userId!),
    enabled:   !!userId,
    staleTime: 30_000,
  });

  // Realtime subscription. If the user connects Delhivery in another tab
  // or via OAuth callback, the demo flag should flip without a reload.
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`user_integrations:${userId}`)
      .on(
        "postgres_changes",
        {
          event:  "*",
          schema: "public",
          table:  "user_integrations",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void queryClient.invalidateQueries({ queryKey: ["demo-mode", userId] });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  // Window focus refetch as a fallback for networks that drop the realtime channel.
  useEffect(() => {
    if (!userId) return;
    const onFocus = () => {
      void queryClient.invalidateQueries({ queryKey: ["demo-mode", userId] });
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId, queryClient]);

  const connectedCount = q.data ?? 0;
  const total          = REQUIRED.length;
  const isDemo         = !!userId && !q.isLoading && connectedCount < total;

  return {
    isDemo,
    connectedCount,
    total,
    loading: !!userId && q.isLoading,
  };
}
